<template>
  <div class="marketplace-checkout--shipping-method">
    <header class="marketplace-checkout--table-header">
      <h2 class="text-xl fw-strong">{{ 'Shipping Method' | t }}</h2>
    </header>
    <SloLoader
      :loading="deliveryModesLoading"
      :class="deliveryModesLoading ? 'border-body' : ''"
      size="xl"
    >
      <div>
        <div class="marketplace-checkout--table-body padding-full">
          <div
            v-if="cartResponse?.cartEntries?.length > 1"
            class="marketplace-checkout--consolidate-shipping mb-lg"
          >
            <div>
              <input
                v-model="isConsolidated"
                id="consolidate-toggle"
                type="checkbox"
                :value="true"
              />
              <label for="consolidate-toggle">
                <span class="text-base fw-strong">{{ 'Consolidate Shipping' | t }}</span>
                <span class="c-gray-700">
                  {{ 'Leave checked to receive orders in as few shipments as possible.' | t }}
                </span>
              </label>
            </div>
          </div>
          <DeliveryModesSelector
            v-if="deliveryModes.length > 0"
            :is-consolidated="isConsolidated"
            :delivery-modes="deliveryModes"
            :selected-delivery-modes="selectedDeliveryModes"
            @updateDeliveryModes="handleUpdateSelectedDeliveryModes"
          />
          <div v-else>
            {{ 'No shipping methods available.' | t }}
          </div>
        </div>
      </div>
      <div class="flex items-center justify-end gap-md mt-lg">
        <SloButton
          @click="$emit('continue', 'back')"
          :disabled="loading"
          type="button"
          fill="outline"
        >
          {{ 'Back' | t }}
        </SloButton>
        <SloButton
          @click="handleContinue"
          :disabled="loading || selectedDeliveryModes.length === 0"
          type="submit"
          class="fixed-button-width"
        >
          <SloLoader :loading="loading" color="white" size="md">
            {{ 'Continue' | t }}
          </SloLoader>
        </SloButton>
      </div>
    </SloLoader>
  </div>
</template>

<script>
import {mapGetters, mapState, mapActions} from 'vuex';
import t from '../../main/i18n';
import {handleApiErrors} from '../../main/utils';
import {marketplaceApi} from '../apis/marketplace';
import SloLoader from '../../../lib/components/SloLoader.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import DeliveryModesSelector from './DeliveryModesSelector.vue';

export default {
  name: 'CheckoutStep3',
  components: {
    SloLoader,
    SloButton,
    DeliveryModesSelector
  },
  filters: {
    t
  },
  props: {
    stepChange: {type: Boolean, default: false}
  },
  data() {
    return {
      loading: false,
      isConsolidated: null,
      deliveryModes: [],
      deliveryModesLoading: true,
      selectedDeliveryModes: {}
    };
  },
  computed: {
    ...mapState('marketplace', ['cartResponse']),
    ...mapGetters('marketplace', ['cartEntries', 'cartHasRestrictedMessages', 'cartDeliveryModes'])
  },
  async created() {
    this.isConsolidated = !(this.cartDeliveryModes && !this.cartResponse?.deliveryMode);
  },
  watch: {
    cartResponse(newValue) {
      if (!newValue.deliveryAddress) {
        this.$emit('continue', 'back');
        this.$snotify.error(t('Please select a shipping address'));
      }
    },
    async isConsolidated() {
      await this.getDeliveryModes();
    }
  },
  methods: {
    ...mapActions('marketplace', ['getCurrentCart']),
    async getDeliveryModes() {
      this.deliveryModesLoading = true;
      await marketplaceApi.getCartDeliveryModes(this.isConsolidated)
        .then(async res => {
          this.deliveryModesLoading = false;
          this.deliveryModes = res.data.deliveryModes;

          if (this.isConsolidated && this.cartDeliveryModes) {
            this.selectedDeliveryModes = this.deliveryModes.reduce((acc, deliveryMode) => {
              acc[deliveryMode.entryNumber] = this.cartResponse.deliveryMode || deliveryMode.modes[0];
              return acc;
            }, {});
          } else if (!this.isConsolidated && this.cartDeliveryModes) {
            this.selectedDeliveryModes = this.deliveryModes.reduce((acc, deliveryMode) => {
              const entryNumbers = deliveryMode.entryNumber.split(',');
              const cartEntry = this.cartEntries.find(_cartEntry =>
                parseInt(_cartEntry.entryNumber) === parseInt(entryNumbers[0])
              );
              acc[deliveryMode.entryNumber] = deliveryMode.modes.find(_deliveryMode => {
                return _deliveryMode.code === cartEntry?.deliveryMode?.code;
              }) || deliveryMode.modes[0];
              return acc;
            }, {});
          } else {
            this.selectedDeliveryModes = this.deliveryModes.reduce((acc, deliveryMode) => {
              acc[deliveryMode.entryNumber] = deliveryMode.modes[0];
              return acc;
            }, {});
          }

          await this.getCurrentCart();
        })
        .catch(e => {
          this.deliveryModesLoading = false;
          this.$emit('continue', 'back');
          handleApiErrors(e, this, 'Unable to get delivery modes');
        });
    },
    handleUpdateSelectedDeliveryModes(selections) {
      this.selectedDeliveryModes = selections;
    },
    async handleContinue() {
      this.loading = true;

      if (this.cartHasRestrictedMessages) {
        this.$emit('restrictedMessagesModalToggle', true);
        this.loading = false;
        return false;
      }

      const deliveryModes = Object.keys(this.selectedDeliveryModes)
        .map(entryNumber => ({
          entryNumber,
          code: this.selectedDeliveryModes[entryNumber].code
        }));

      await marketplaceApi.selectCartDeliveryModes(deliveryModes)
        .then(() => this.$emit('continue'))
        .catch(e => {
          this.loading = false;
          handleApiErrors(e, this, 'Unable to select delivery mode');
        });
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../../scss/variables';
@import '../../../scss/system';

.marketplace-checkout {
  &--consolidate-shipping {
    > div {
      display: flex;
      align-items: center;
      justify-content: start;
      gap: 2.4rem;
      margin-block-end: 2.4rem;
    }

    label {
      display: flex;
      flex-direction: column;
      align-items: start;
      justify-content: start;
      gap: 0.4rem;
      color: initial;
      font-size: initial;
      font-weight: initial;
      margin-bottom: 0;
    }

    input {
      min-width: 1.6rem;
      min-height: 1.6rem;
      margin: 0;

      &:checked {
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        border-radius: 2px;
        background-color: $color-blue-400;

        &:after {
          content: '';
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
          width: 1.2rem;
          height: 1.6rem;
          background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="white" width="18px" height="18px"><path d="M470.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L192 338.7 425.4 105.4c12.5-12.5 32.8-12.5 45.3 0z" /></svg>');
          background-repeat: no-repeat;
          background-position: center;
          background-size: contain;
        }
      }
    }
  }

  &--table-header {
    border: 1px solid $color-gray-400;
    border-radius: 1.2rem 1.2rem 0 0;
  }

  &--table-body {
    border: 1px solid $color-gray-400;
    border-top: none;
    border-radius: 0 0 1.2rem 1.2rem;
  }
}

.border-body {
  padding: 2.4rem;
  border-inline: 1px solid $color-gray-400;
  border-bottom: 1px solid $color-gray-400;
  border-radius: 0 0 1.2rem 1.2rem;
}

.fixed-button-width {
  min-width: 96px;
}
</style>
